
import { useCallback, useEffect } from 'react';
import { useApp } from "../../context/AppContext";

// "Add Text to PDF" boxes are styled as TEXT TYPED ONTO THE PAGE — no yellow paper,
// no header bar — so they can't be mistaken for the floating yellow Sticky Notes.
const TEXT_BOX_WIDTH = 220;
const TEXT_ACCENT = "#2563eb";
// annot.color is the TEXT colour (older notes stored a sticky-note background
// colour there; anything unknown falls back to black)
const TEXT_COLORS = ["#111827", "#2563eb", "#dc2626", "#16a34a"];
const getTextColor = (color) => (TEXT_COLORS.includes(color) ? color : TEXT_COLORS[0]);

// On the page: a faint dashed outline only. While editing: solid accent outline + toolbar.
const applyTextBoxMode = (div, editing) => {
    div.style.background = editing ? "rgba(255,255,255,0.95)" : "rgba(255,255,255,0.35)";
    div.style.border = editing ? `1.5px solid ${TEXT_ACCENT}` : "1px dashed rgba(37,99,235,0.45)";
    div.style.boxShadow = editing ? "0 0 0 3px rgba(37,99,235,0.15)" : "none";
    const bar = div.querySelector('[data-role="text-toolbar"]');
    if (bar) bar.style.display = editing ? "flex" : "none";
};

export const usePdfText = (containerRef, getCurrentPageNum, contentRef = null) => {
    const {
        pdfAnnotations,
        setPdfAnnotations,
        handleDeletePdfText: onDeletePdfText,
        setDeletedPdfTexts,
        setIsDirty,
    } = useApp();

    // 🎨 renderPdfAnnotation: Renders an "Add Text to PDF" text box on a page wrapper
    const renderPdfAnnotation = useCallback((wrapper, annot) => {
        const id = `pdf-annot-${annot.id}`;
        let div = wrapper.querySelector(`[data-annot-id="${id}"]`);

        if (!div) {
            div = document.createElement("div");
            div.dataset.annotId = id;
            div.style.position = "absolute";
            div.style.zIndex = "1000";
            // The note lives inside the zoomed PDF. When zoomed OUT (e.g. ~40% on iPad
            // portrait) it would shrink to an unusable ~70px with 4px buttons, so scale it
            // back up to its real size; when zoomed IN it grows with the PDF as before.
            div.style.transform = "translate(-50%, -50%) scale(max(1, calc(1 / var(--pdf-zoom, 1))))";
            wrapper.appendChild(div);
        }

        const textColor = getTextColor(annot.color);

        // Position
        div.style.left = `${annot.xPct * 100}%`;
        div.style.top = `${annot.yPct * 100}%`;

        // Text box base styles
        div.style.width = `${TEXT_BOX_WIDTH}px`;
        div.style.borderRadius = "4px";
        div.style.boxSizing = "border-box";
        applyTextBoxMode(div, !!annot.isEditing);
        div.style.userSelect = "none";
        div.style.webkitUserSelect = "none";
        div.style.webkitTouchCallout = "none"; // no iOS long-press menu on the note
        div.style.pointerEvents = "auto";
        div.style.touchAction = annot.isEditing ? "auto" : "none";
        div.style.cursor = annot.isEditing ? "default" : "grab";
        div.style.overflow = "visible"; // the toolbar and "T" badge sit outside the box

        const updatePosition = (left, top) => {
            div.style.left = left;
            div.style.top = top;
        };

        const updateAnnotations = (updater) => {
            setPdfAnnotations(updater);
            if (setIsDirty) setIsDirty(true);
        };

        // Drag logic
        const handleStart = (clientX, clientY) => {
            const wrapperRect = wrapper.getBoundingClientRect();
            const startX = clientX;
            const startY = clientY;
            const initialXPct = annot.xPct;
            const initialYPct = annot.yPct;
            let hasMoved = false;
            div.style.cursor = "grabbing";
            div.style.zIndex = "1001";

            const onMove = (moveX, moveY) => {
                const dx = moveX - startX;
                const dy = moveY - startY;
                if (!hasMoved && (Math.abs(dx) > 3 || Math.abs(dy) > 3)) {
                    hasMoved = true;
                    div.dataset.dragging = "true";
                }
                if (hasMoved) {
                    const cx = Math.max(0, Math.min(1, initialXPct + dx / wrapperRect.width));
                    const cy = Math.max(0, Math.min(1, initialYPct + dy / wrapperRect.height));
                    updatePosition(`${cx * 100}%`, `${cy * 100}%`);
                }
            };

            const onEnd = (endX, endY) => {
                div.style.cursor = annot.isEditing ? "default" : "grab";
                div.style.zIndex = "1000";
                if (hasMoved) {
                    const dx = endX - startX;
                    const dy = endY - startY;
                    const fx = Math.max(0, Math.min(1, initialXPct + dx / wrapperRect.width));
                    const fy = Math.max(0, Math.min(1, initialYPct + dy / wrapperRect.height));
                    updateAnnotations(prev => prev.map(a =>
                        a.id === annot.id ? { ...a, xPct: fx, yPct: fy } : a
                    ));
                    setTimeout(() => delete div.dataset.dragging, 50);
                }
            };
            return { onMove, onEnd };
        };

        // Drag — Pointer Events cover mouse, finger and Apple Pencil alike. touch-action
        // (set above / on the header) is what stops iPad Safari from scrolling instead.
        div.onpointerdown = (e) => {
            if (e.pointerType === "mouse" && e.button !== 0) return;
            const t = e.target;
            if (t.closest("button, [data-color-dot]")) return; // incl. the icon inside a button
            // While editing, touching the text area places the caret instead of dragging
            if (t.tagName === "TEXTAREA" && !t.readOnly) return;
            if (e.pointerType === "mouse") e.preventDefault(); // no text selection while dragging
            e.stopPropagation();

            const pointerId = e.pointerId;
            const { onMove, onEnd } = handleStart(e.clientX, e.clientY);
            let lastX = e.clientX;
            let lastY = e.clientY;
            let captured = false;
            // Pressed on the grab margin, handle or toolbar (not the text): capture right
            // away — otherwise moves that leave this small area go to the page (drawing
            // ink in Pen mode) and the box never follows. Only a press on the text needs
            // late capture, so its tap still reaches the textarea's click (tap-to-edit).
            if (t.tagName !== "TEXTAREA") {
                captured = true;
                try { div.setPointerCapture(pointerId); } catch { /* element may be detached */ }
            }

            const handleMove = (ev) => {
                if (ev.pointerId !== pointerId) return;
                lastX = ev.clientX;
                lastY = ev.clientY;
                // Capture only once it's really a drag: capturing on pointerdown would
                // retarget the click of a plain tap to this div, and the textarea's
                // tap-to-edit handler would never run.
                if (!captured && (Math.abs(lastX - e.clientX) > 3 || Math.abs(lastY - e.clientY) > 3)) {
                    captured = true;
                    try { div.setPointerCapture(pointerId); } catch { /* element may be detached */ }
                }
                onMove(lastX, lastY);
            };
            const handleUp = (ev) => {
                if (ev.pointerId !== pointerId) return;
                div.removeEventListener("pointermove", handleMove);
                div.removeEventListener("pointerup", handleUp);
                div.removeEventListener("pointercancel", handleUp);
                onEnd(lastX, lastY);
            };
            div.addEventListener("pointermove", handleMove);
            div.addEventListener("pointerup", handleUp);
            div.addEventListener("pointercancel", handleUp);
        };

        // Rebuild inner content. If the user is typing in this note, remember the
        // caret so the fresh textarea can pick up where they left off.
        const prevTextarea = div.querySelector("textarea");
        const hadFocus = !!prevTextarea && document.activeElement === prevTextarea;
        // Only the text changed while typing → the DOM is already correct. Skipping the
        // rebuild keeps IME composition (e.g. Hindi input) and undo history intact.
        if (hadFocus && annot.isEditing && div.dataset.color === textColor) return;
        div.dataset.color = textColor;
        const caret = hadFocus ? [prevTextarea.selectionStart, prevTextarea.selectionEnd] : null;
        div._rebuilding = true; // removing a focused textarea may fire blur — ignore it
        div.innerHTML = "";
        div._rebuilding = false;

        // ── Invisible 14px grab margin around the box: a Pencil/finger press just outside
        // the box (or in the gap to its toolbar) grabs the box instead of drawing ink on the
        // page when the Pen tool is active. Pointer events on it go to the div's drag handler.
        const hitSlop = document.createElement("div");
        hitSlop.dataset.role = "hit-slop";
        hitSlop.style.cssText = "position: absolute; inset: -14px; z-index: -1; background: transparent;";
        div.appendChild(hitSlop);

        // ── "T" badge: marks this as PDF text (vs. a yellow sticky note) ──
        const badge = document.createElement("div");
        badge.textContent = "T";
        badge.title = "Text added to PDF";
        badge.style.cssText = `
            position: absolute; top: -9px; left: -9px; width: 18px; height: 18px;
            border-radius: 50%; background: ${TEXT_ACCENT}; color: #fff;
            font: 700 11px/18px Georgia, "Times New Roman", serif; text-align: center;
            box-shadow: 0 1px 3px rgba(0,0,0,0.25); pointer-events: none;
        `;
        div.appendChild(badge);

        // ── Toolbar above the box (only while editing): move · text colours · delete ──
        const toolbar = document.createElement("div");
        toolbar.dataset.role = "text-toolbar";
        toolbar.style.cssText = `
            position: absolute; left: 0; bottom: calc(100% + 4px);
            display: ${annot.isEditing ? "flex" : "none"}; align-items: center; gap: 6px;
            padding: 2px 6px; background: #fff; border: 1px solid #e5e7eb; border-radius: 8px;
            box-shadow: 0 4px 12px rgba(0,0,0,0.12); white-space: nowrap; touch-action: none;
        `;

        // Pressing anything on the toolbar must not take focus from the text: with
        // finger / Apple Pencil that blur ended editing and redrew the box, removing the
        // delete button before its click arrived (preventDefault on pointerdown keeps focus).
        toolbar.addEventListener("pointerdown", (e) => {
            if (!e.target.closest("[data-color-dot]") && e.target !== handle) e.preventDefault();
        });

        const handle = document.createElement("span");
        handle.textContent = "⠿";
        handle.title = "Drag to move";
        // Finger / Apple Pencil sized targets (~30px): with the Pen tool active, a near-miss
        // on a tiny control lands on the page and draws ink instead of moving the box.
        handle.style.cssText = `
            cursor: grab; color: #6b7280; font-size: 18px; line-height: 1;
            width: 30px; height: 30px; display: flex; align-items: center; justify-content: center;
        `;
        toolbar.appendChild(handle);

        TEXT_COLORS.forEach(c => {
            const dot = document.createElement("div");
            const isActive = c === textColor;
            dot.title = "Text colour";
            dot.style.cssText = `
                width: 22px; height: 22px; border-radius: 50%; cursor: pointer; flex-shrink: 0;
                background: ${c}; border: 2px solid #fff;
                box-shadow: 0 0 0 ${isActive ? "2px " + TEXT_ACCENT : "1px #d1d5db"};
            `;
            dot.dataset.colorDot = "true";
            dot.onpointerdown = (e) => {
                e.stopPropagation();
                e.preventDefault(); // keep focus in the textarea (mouse)
                updateAnnotations(prev => prev.map(a =>
                    a.id === annot.id ? { ...a, color: c } : a
                ));
            };
            toolbar.appendChild(dot);
        });

        const sep = document.createElement("span");
        sep.style.cssText = "width: 1px; height: 16px; background: #e5e7eb;";
        toolbar.appendChild(sep);

        const delBtn = document.createElement("button");
        delBtn.innerHTML = '<i class="bi bi-trash3"></i>';
        delBtn.title = "Delete text";
        delBtn.style.cssText = `
            background: none; border: none; cursor: pointer; color: #dc2626;
            font-size: 16px; line-height: 1; width: 30px; height: 30px; padding: 0;
            display: flex; align-items: center; justify-content: center;
        `;
        delBtn.onclick = (e) => {
            e.stopPropagation();
            if (onDeletePdfText) onDeletePdfText(annot.id);
        };
        toolbar.appendChild(delBtn);
        div.appendChild(toolbar);

        // ── Textarea ──
        const textarea = document.createElement("textarea");
        textarea.value = annot.text;
        textarea.placeholder = "Type text…";
        textarea.rows = 1;
        textarea.style.cssText = `
            display: block;
            width: 100%;
            min-height: 34px;
            resize: none;
            border: none;
            outline: none;
            background: transparent;
            font-size: 15px;
            line-height: 1.4;
            padding: 6px 8px;
            color: ${textColor};
            font-family: Helvetica, Arial, sans-serif;
            overflow: hidden;
            box-sizing: border-box;
            cursor: ${annot.isEditing ? "text" : "grab"};
            -webkit-user-select: text;
            user-select: text;
        `;

        if (!annot.isEditing) {
            textarea.readOnly = true;
        }

        // Tap / click (without dragging) opens the note for editing. A single tap, not a
        // double-click: on iPad a double-tap is taken by Safari and dblclick rarely fires.
        // focus() must run right here, inside the tap, or iOS won't show the keyboard.
        textarea.onclick = (e) => {
            if (!textarea.readOnly) return; // already editing
            if (div.dataset.dragging) return; // this click ended a drag
            e.stopPropagation();
            textarea.readOnly = false;
            textarea.style.cursor = "text";
            div.style.touchAction = "auto";
            applyTextBoxMode(div, true);
            textarea.focus();
            const end = textarea.value.length;
            textarea.setSelectionRange(end, end);
            updateAnnotations(prev => prev.map(a =>
                a.id === annot.id ? { ...a, isEditing: true } : a
            ));
        };

        // The box grows with its text instead of being a fixed square
        const autoGrow = () => {
            textarea.style.height = "auto";
            textarea.style.height = `${textarea.scrollHeight}px`;
        };

        // Keep state in sync while typing so autosave / re-renders never see stale text
        textarea.oninput = () => {
            autoGrow();
            updateAnnotations(prev => prev.map(a =>
                a.id === annot.id ? { ...a, text: textarea.value } : a
            ));
        };

        textarea.onblur = () => {
            if (div._rebuilding) return;
            const newText = textarea.value.trim();
            if (!newText) {
                // Silently remove empty note without confirm dialog
                if (!String(annot.id).startsWith('pdf-annot-')) {
                    setDeletedPdfTexts(prev => [...prev, annot.id]);
                }
                updateAnnotations(prev => prev.filter(a => String(a.id) !== String(annot.id)));
                return;
            }
            textarea.readOnly = true;
            textarea.style.cursor = "grab";
            updateAnnotations(prev => prev.map(a =>
                a.id === annot.id ? { ...a, text: textarea.value, isEditing: false } : a
            ));
        };

        textarea.onkeydown = (e) => {
            if (e.key === "Escape") textarea.blur();
        };

        div.appendChild(textarea);
        autoGrow();

        if (hadFocus && annot.isEditing) {
            textarea.focus();
            textarea.setSelectionRange(caret[0], caret[1]);
        } else if (annot.isEditing && !prevTextarea) {
            // Newly created note → focus it synchronously. When this runs inside the
            // toolbar tap (see addPdfText), iOS accepts it and opens the keyboard.
            textarea.readOnly = false;
            textarea.focus();
        }

    }, [setPdfAnnotations, onDeletePdfText, setDeletedPdfTexts, setIsDirty]);

    // 🔄 Update DOM when annotations change
    useEffect(() => {
        const container = containerRef.current;
        const contentContainer = contentRef ? contentRef.current : container;
        if (!container || !contentContainer) return;

        // Remove annotations no longer in state
        const allRendered = contentContainer.querySelectorAll('[data-annot-id^="pdf-annot-"]');
        allRendered.forEach(el => {
            const id = el.dataset.annotId.replace('pdf-annot-', '');
            if (!pdfAnnotations.some(a => String(a.id) === id)) {
                el.remove();
            }
        });

        pdfAnnotations.forEach(a => {
            const wrapper = contentContainer.querySelector(`.pdf-page-wrapper[data-page-number="${a.pageNum}"]`);
            if (wrapper && wrapper.dataset.loaded === "true") {
                renderPdfAnnotation(wrapper, a);
            }
        });
    }, [pdfAnnotations, renderPdfAnnotation, containerRef, contentRef]);


    // Legacy: place note at center of current page (called from toolbar button)
    const addPdfText = useCallback(() => {
        const pageNum = getCurrentPageNum ? getCurrentPageNum() : 1;
        const contentContainer = (contentRef && contentRef.current) || containerRef.current;
        const wrapper = contentContainer?.querySelector(`.pdf-page-wrapper[data-page-number="${pageNum}"]`);

        // Don't stack new boxes on top of each other: step down past any box already
        // at the drop spot. The step is one box + toolbar (~90px on screen); boxes keep
        // their real size when zoomed out, so convert using the current zoom.
        let step = 0.06;
        if (wrapper && wrapper.offsetHeight > 0) {
            const zoom = wrapper.getBoundingClientRect().height / wrapper.offsetHeight || 1;
            step = Math.max(0.06, (90 * Math.max(1, 1 / zoom)) / wrapper.offsetHeight);
        }
        const takenY = pdfAnnotations
            .filter(a => a.pageNum === pageNum && Math.abs(a.xPct - 0.5) < 0.05)
            .map(a => a.yPct);
        let yPct = 0.3;
        for (let i = 0; i < 12; i++) {
            const y = 0.3 + i * step;
            if (y > 0.95) break;
            if (!takenY.some(t => Math.abs(t - y) < step * 0.9)) { yPct = y; break; }
        }
        const newAnnot = {
            id: `pdf-annot-${Date.now()}`,
            pageNum,
            xPct: 0.5,
            yPct,
            text: "",
            isEditing: true
        };
        // Draw + focus the note right now, inside the button's tap handler. If we waited
        // for the state → effect cycle, iOS Safari would refuse the focus (not a user
        // gesture any more) and the keyboard would never open.
        if (wrapper && wrapper.dataset.loaded === "true") {
            renderPdfAnnotation(wrapper, newAnnot);
        }

        setPdfAnnotations(prev => [...prev, newAnnot]);
        if (setIsDirty) setIsDirty(true);
    }, [getCurrentPageNum, pdfAnnotations, setPdfAnnotations, setIsDirty, renderPdfAnnotation, containerRef, contentRef]);

    return { renderPdfAnnotation, addPdfText };
};
